'use client'

import { useEffect, useState } from "react";
import useSessionUser from "@/common/hooks/useSessionUser";
import { detectBrowser, detectDevice, urlBase64ToUint8Array } from "@/utils/utils.ts";
import { updatePushSubscriptionAction } from "@/app/actions/user.ts";
import { UpdatePushSubscriptionParams } from "@/types/types.ts";

const SW_PUSH_CACHE_VERSION = '2';

export default function PushNotificationProvider() {
  const user = useSessionUser();
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);

  useEffect(() => {
    // console.log('[PushNotifications] hasRequestedPermission: ', hasRequestedPermission)

    if (!hasRequestedPermission) {
      try {
        registerServiceWorkerAndSubscribe();
      } catch (error) {
        console.log('[PushNotifications] error registerServiceWorkerAndSubscribe: ', error)
      }
    }

    const interactionEvents = [
      'click',
      'touchstart'
    ];
    const handleInteraction = () => {
      // console.log('[PushNotifications] Permission: ', Notification.permission)
      if (!hasRequestedPermission) {
        requestPermissionOnInteraction();
      }
    };

    interactionEvents.forEach(event => {
      document.addEventListener(event, handleInteraction, { once: true });
    });

    return () => {
      interactionEvents.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [
    user,
    hasRequestedPermission
  ]);

  const requestPermissionOnInteraction = () => {
    Notification.requestPermission().then(permission => {
      // console.log(`[PushNotifications] Permission result: ${permission}`);
      if (permission === 'granted') {
        registerServiceWorkerAndSubscribe();
      }
    });
  };

  const registerServiceWorkerAndSubscribe = async () => {
    try {
      // @note: SW_PUSH_CACHE_VERSION is also set at sw-push.js
      const swPath = `/sw-push.js?v=${SW_PUSH_CACHE_VERSION}`;
      const registration = await navigator.serviceWorker.register(swPath);

      // console.log('[PushNotifications] Service Worker registration:', registration);

      let subscription = await registration.pushManager.getSubscription();

      // console.log('[PushNotifications] Using subscription', subscription);

      if (!subscription) {
        const vapidKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

        if (!vapidKey) {
          throw new Error('[PushNotifications] VAPID public key not found');
        }

        // console.log('[PushNotifications] VAPID Key:', vapidKey);
        const applicationServerKey = urlBase64ToUint8Array(vapidKey);
        // console.log('[PushNotifications] Converted Key:', applicationServerKey);

        if (!registration.pushManager) {
          throw new Error('[PushNotifications] PushManager not available');
        }

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });

        // console.log('[PushNotifications] New subscription created: ', subscription);
      } else {
        // console.log('[PushNotifications] Using existing subscription');
      }

      // console.log('[PushNotifications] Using subscription', subscription);

      if (subscription && user?.id) {
        const browserInfo = detectBrowser();
        const deviceInfo = detectDevice();
        const subscriptionData = subscription.toJSON();
        // console.log('[PushNotifications] Using subscriptionData', subscriptionData);

        await updatePushSubscriptionAction({
          userId: user.id,
          subscriptionData: subscriptionData,
          browserInfo: browserInfo,
          deviceInfo: deviceInfo
        } as UpdatePushSubscriptionParams);

        // console.log('[PushNotifications] Using existing subscription');
        console.log('[PushNotifications] Subscription saved to database', subscriptionData);

        setHasRequestedPermission(true);
      }
    } catch (error) {
      console.error('[Push] Service worker registration failed:', error);
    }
  };

  return null;
}
